import React, { useCallback, useRef, useEffect } from "react";
import { Handle, NodeProps, Position, useReactFlow } from "reactflow";
import { Link } from "react-router-dom";
import { GripVertical, Waypoints, Plus, Trash2 } from "lucide-react";
import EntityAvatar from "components/Entities/EntityAvatar";
import type { GraphIssueNodeData } from "./GraphIssueNodeTypes";
import { formatGraphMessage, graphMessage } from "./graphCopy";

export type { GraphIssueNodeData } from "./GraphIssueNodeTypes";

/** Fixed node width for consistent sizing across all nodes. */
const NODE_WIDTH = 200;
const KEYBOARD_NUDGE_STEP = 32;

/**
 * Renders a graph node for an issue.
 *
 * Layout strategy:
 * - Desktop: Handles on Left (inbound) / Right (outbound).
 *   Badge + add button clusters sit to the LEFT of the left handle
 *   and to the RIGHT of the right handle so they never overlap edges.
 * - Mobile: Handles on Top (inbound) / Bottom (outbound).
 *   Badge + add button clusters sit ABOVE the top handle
 *   and BELOW the bottom handle.
 * - In `editable` mode, secondary handles appear on the two remaining
 *   sides so drag-to-connect works in any direction without changing the
 *   dominant layout axis.
 */
const GraphIssueNode: React.FC<NodeProps<GraphIssueNodeData>> = ({ data, selected }) => {
  const {
    entity,
    additionalInfluencersCount,
    additionalInfluencedCount,
    totalInfluencers,
    totalInfluenced,
    onExpandInfluencers,
    onExpandInfluenced,
    onAddInfluencer,
    onAddInfluenced,
    onGroupDragDelta,
    onRemoveFromCollection,
    isMobile = false,
    editable = false,
    sourceStyle = null,
    locale = "en-US",
  } = data;

  const { screenToFlowPosition } = useReactFlow();
  const lastScreenRef = useRef<{ x: number; y: number } | null>(null);

  const handleGripPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!onGroupDragDelta) return;
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      lastScreenRef.current = { x: e.clientX, y: e.clientY };
    },
    [onGroupDragDelta]
  );

  const handleGripPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!onGroupDragDelta || !lastScreenRef.current) return;
      const prev = screenToFlowPosition(lastScreenRef.current);
      const curr = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const dx = curr.x - prev.x;
      const dy = curr.y - prev.y;
      if (Math.abs(dx) > 0 || Math.abs(dy) > 0) {
        onGroupDragDelta(dx, dy);
        lastScreenRef.current = { x: e.clientX, y: e.clientY };
      }
    },
    [onGroupDragDelta, screenToFlowPosition]
  );

  const handleGripPointerUp = useCallback(
    (e: React.PointerEvent) => {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      lastScreenRef.current = null;
    },
    []
  );

  useEffect(() => {
    return () => {
      lastScreenRef.current = null;
    };
  }, []);

  if (!entity) return null;

  /* ─── Reusable sub-elements ─── */

  /** Expand badge: Waypoints icon + count. Clickable when there are hidden/unloaded nodes. */
  const influenceBadge = (
    count: number,
    additional: number,
    direction: "in" | "out"
  ) => {
    if (count <= 0) return null;
    const canExpand = additional > 0;
    const directionLabel = graphMessage(
      locale,
      direction === "in" ? "influencersLabel" : "influencedLabel"
    );
    const loadMoreLabel = canExpand
      ? `, ${formatGraphMessage(locale, "loadMoreConnections", { count: additional })}`
      : "";
    return (
      <button
        type="button"
        disabled={!canExpand}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (direction === "in") onExpandInfluencers(entity.id);
          else onExpandInfluenced(entity.id);
        }}
        className={`nodrag graph-issue-badge graph-issue-badge-${direction} flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none transition-colors ${
          canExpand
            ? "bg-eoe-icon dark:bg-eoe-dark-icon hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text dark:text-eoe-dark-text cursor-pointer"
            : "bg-eoe-border-main/60 dark:bg-eoe-dark-border-main/60 text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-default"
        }`}
        aria-label={`${count} ${directionLabel}${loadMoreLabel}`}
        title={`${count} ${directionLabel}${loadMoreLabel}`}
      >
        <Waypoints className="size-2.5 shrink-0" />
        <span>{count}</span>
      </button>
    );
  };

  /** Add-influence + button */
  const addBtn = (
    onClick: (() => void) | undefined,
    label: string
  ) => {
    if (!onClick) return null;
    return (
      <button
        type="button"
        className="nodrag graph-issue-node-add flex items-center justify-center size-5 rounded-full bg-eoe-icon dark:bg-eoe-dark-icon hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text dark:text-eoe-dark-text shrink-0 transition-colors"
        aria-label={label}
        title={label}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }}
      >
        <Plus className="size-3" />
      </button>
    );
  };

  const gripBlock = (
    <button
      type="button"
      className="nodrag graph-issue-node-grip flex items-center justify-center border-0 bg-transparent p-0 text-eoe-text-sub/50 transition-colors hover:text-eoe-text-sub active:cursor-grabbing dark:text-eoe-dark-text-sub/50 dark:hover:text-eoe-dark-text-sub cursor-grab touch-none"
      aria-label={graphMessage(locale, "dragGroupAria")}
      title={graphMessage(locale, "dragGroupTitle")}
      onPointerDown={handleGripPointerDown}
      onPointerMove={handleGripPointerMove}
      onPointerUp={handleGripPointerUp}
      onPointerCancel={handleGripPointerUp}
      onKeyDown={(e) => {
        if (!onGroupDragDelta) {
          return;
        }
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          onGroupDragDelta(-KEYBOARD_NUDGE_STEP, 0);
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          onGroupDragDelta(KEYBOARD_NUDGE_STEP, 0);
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          onGroupDragDelta(0, -KEYBOARD_NUDGE_STEP);
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          onGroupDragDelta(0, KEYBOARD_NUDGE_STEP);
        }
      }}
    >
      <GripVertical className="size-3 pointer-events-none" />
    </button>
  );

  const removeBlock = onRemoveFromCollection ? (
    <button
      type="button"
      className="nodrag graph-issue-node-remove absolute -top-2 -right-2 z-20 flex items-center justify-center size-5 rounded-full border border-warning-red/60 bg-eoe-bg text-warning-red shadow-sm opacity-0 transition-opacity hover:bg-warning-red/10 focus-visible:opacity-100 group-hover:opacity-100 dark:border-dark-warning-red/60 dark:bg-eoe-dark-bg dark:text-dark-warning-red"
      aria-label={graphMessage(locale, "removeIssueCta")}
      title={graphMessage(locale, "removeIssueCta")}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onRemoveFromCollection();
      }}
    >
      <Trash2 className="size-3" />
    </button>
  ) : null;

  const avatarBlock = (
    <Link
      to={`/issue/${encodeURIComponent(entity.cname)}`}
      className="nodrag graph-issue-node-link flex items-center gap-0 no-underline"
    >
      <EntityAvatar
        entityType="issue"
        entity={entity}
        entityChip={true}
        isInputField={false}
        showLink={false}
        isGeneralEntity={true}
        showCloseChip={false}
        className="graph-issue-node-avatar"
      />
    </Link>
  );

  // Source ring: if the node belongs to one or more remix sources, layer
  // colored rings behind the card so overlapping sources show a banded
  // outline. When `isOverlap` is true we add a second, slightly offset ring.
  const sourceRingStyle: React.CSSProperties | undefined = sourceStyle && sourceStyle.outlineColors.length > 0
    ? sourceStyle.isOverlap && sourceStyle.outlineColors.length > 1
      ? {
          boxShadow: [
            `0 0 0 2px ${sourceStyle.outlineColors[0]}`,
            `0 0 0 4px ${sourceStyle.outlineColors[1]}`,
          ].join(", "),
        }
      : {
          boxShadow: `0 0 0 2px ${sourceStyle.outlineColors[0]}`,
        }
    : undefined;

  const handleClassName = "graph-issue-node-handle !w-2 !h-2 !border-2 !bg-transparent !border-eoe-border-main dark:!border-eoe-dark-border-main";

  /* ─── Mobile layout: handles top/bottom, clusters above/below ─── */
  if (isMobile) {
    return (
      <div
        className={`graph-issue-node graph-issue-node-mobile relative flex flex-col items-center group ${selected ? "is-selected" : ""}`}
        style={{ width: NODE_WIDTH }}
      >
        {/* Top cluster: add-influencer + influencer badge (above the node + handle) */}
        <div className="graph-issue-node-top-cluster flex flex-col items-center gap-1 mb-3">
          {addBtn(onAddInfluencer, graphMessage(locale, "addInfluencer"))}
          {influenceBadge(totalInfluencers, additionalInfluencersCount, "in")}
        </div>

        {/* Node card with handles */}
        <div
          className={`graph-issue-node-card relative w-full rounded-xl bg-eoe-bg dark:bg-eoe-dark-bg border border-eoe-border-main dark:border-eoe-dark-border-main shadow-sm`}
          style={sourceRingStyle}
        >
          {removeBlock}
          <Handle
            id="target-top"
            type="target"
            position={Position.Top}
            isConnectable={editable}
            className={`${handleClassName} graph-issue-node-handle-in`}
          />
          {editable ? (
            <Handle
              id="target-left"
              type="target"
              position={Position.Left}
              isConnectable
              className={`${handleClassName} graph-issue-node-handle-alt`}
            />
          ) : null}
          <div className="graph-issue-node-body flex flex-col items-center gap-1 px-2 py-2 w-full">
            <div className="graph-issue-node-toolbar flex items-center justify-center w-full">
              {gripBlock}
            </div>
            {avatarBlock}
          </div>
          {editable ? (
            <Handle
              id="source-right"
              type="source"
              position={Position.Right}
              isConnectable
              className={`${handleClassName} graph-issue-node-handle-alt`}
            />
          ) : null}
          <Handle
            id="source-bottom"
            type="source"
            position={Position.Bottom}
            isConnectable={editable}
            className={`${handleClassName} graph-issue-node-handle-out`}
          />
        </div>

        {/* Bottom cluster: influenced badge + add-influenced (below the node + handle) */}
        <div className="graph-issue-node-bottom-cluster flex flex-col items-center gap-1 mt-1">
          {influenceBadge(totalInfluenced, additionalInfluencedCount, "out")}
          {addBtn(onAddInfluenced, graphMessage(locale, "addInfluenced"))}
        </div>
      </div>
    );
  }

  /* ─── Desktop layout: handles left/right, clusters beside them ─── */
  return (
    <div
      className={`graph-issue-node graph-issue-node-desktop relative flex flex-row items-center group ${selected ? "is-selected" : ""}`}
      style={{ width: NODE_WIDTH }}
    >
      {/* Left cluster: add-influencer + influencer badge (to the left of the node) */}
      <div className="graph-issue-node-left-cluster absolute right-full top-1/2 -translate-y-1/2 flex items-center gap-1 pr-2 z-10">
        {addBtn(onAddInfluencer, graphMessage(locale, "addInfluencer"))}
        {influenceBadge(totalInfluencers, additionalInfluencersCount, "in")}
      </div>

      {/* Node card with handles */}
      <div
        className="graph-issue-node-card relative w-full rounded-xl bg-eoe-bg dark:bg-eoe-dark-bg border border-eoe-border-main dark:border-eoe-dark-border-main shadow-sm"
        style={sourceRingStyle}
      >
        {removeBlock}
        <Handle
          id="target-left"
          type="target"
          position={Position.Left}
          isConnectable={editable}
          className={`${handleClassName} graph-issue-node-handle-in`}
        />
        {editable ? (
          <Handle
            id="target-top"
            type="target"
            position={Position.Top}
            isConnectable
            className={`${handleClassName} graph-issue-node-handle-alt`}
          />
        ) : null}
        <div className="graph-issue-node-body flex flex-col items-center gap-1 px-2 py-2 w-full">
          <div className="graph-issue-node-toolbar flex items-center justify-center w-full">
            {gripBlock}
          </div>
          {avatarBlock}
        </div>
        {editable ? (
          <Handle
            id="source-bottom"
            type="source"
            position={Position.Bottom}
            isConnectable
            className={`${handleClassName} graph-issue-node-handle-alt`}
          />
        ) : null}
        <Handle
          id="source-right"
          type="source"
          position={Position.Right}
          isConnectable={editable}
          className={`${handleClassName} graph-issue-node-handle-out`}
        />
      </div>

      {/* Right cluster: influenced badge + add-influenced (to the right of the node) */}
      <div className="graph-issue-node-right-cluster absolute left-full top-1/2 -translate-y-1/2 flex items-center gap-1 pl-2 z-10">
        {influenceBadge(totalInfluenced, additionalInfluencedCount, "out")}
        {addBtn(onAddInfluenced, graphMessage(locale, "addInfluenced"))}
      </div>
    </div>
  );
};

export default GraphIssueNode;
